import { formatPhone } from '../../shared/phone';
import { localDay } from '../../shared/time';
import type { Tone } from '../../ui/v2';

/**
 * Inquiry model for the events and catering pages.
 *
 * The backend tracks two things per inquiry: the coordinator-email pipeline
 * (`status`: pending | processing | sent | failed) and the stage
 * (`dashboard_state`). Lost is stored as `hidden` so older rows stay lost.
 * Won and archived are the other closed stages. The stage shown in the UI
 * is changed through PATCH …/dashboard-state/, so every stage here persists.
 */

export type Stage = 'new' | 'to_contact' | 'won' | 'lost' | 'archived';
export type DashboardState = 'unseen' | 'seen' | 'won' | 'hidden' | 'archived';

export const STAGES: Stage[] = ['new', 'to_contact', 'won', 'lost', 'archived'];

export const STAGE_LABEL: Record<Stage, string> = {
  new: 'New',
  to_contact: 'To contact',
  won: 'Won',
  lost: 'Lost',
  archived: 'Archived',
};

export const STAGE_TONE: Record<Stage, Tone> = {
  new: 'accent',
  to_contact: 'warning',
  won: 'success',
  lost: 'danger',
  archived: 'neutral',
};

export const STAGE_STATE: Record<Stage, DashboardState> = {
  new: 'unseen',
  to_contact: 'seen',
  won: 'won',
  lost: 'hidden',
  archived: 'archived',
};

/** Still in the working list. Won, lost and archived are filed away. */
export function isOpenStage(stage: Stage): boolean {
  return stage === 'new' || stage === 'to_contact';
}

export type CoordinatorEmailStatus =
  | 'pending'
  | 'processing'
  | 'sent'
  | 'failed';

export interface CoordinatorEmail {
  status: CoordinatorEmailStatus;
  subject: string | null;
  body: string | null;
  recipients: string[];
  sentAt: string | null;
  error: string | null;
}

export type ActivityKind = 'call' | 'email' | 'note' | 'system';

export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  at: string;
  actor: 'arthur' | 'system' | { name: string };
  title: string;
  body?: string | null;
  transcript?: string | null;
  meta?: Record<string, string | undefined>;
}

export interface ExtraField {
  key: string;
  label: string;
  value: string;
}

export interface InquiryRecord {
  id: number;
  guestName: string | null;
  phone: string | null;
  email: string | null;
  preferredContact: string | null;
  occasion: string | null;
  /** yyyy-mm-dd when the free-text date could be parsed. */
  eventDate: string | null;
  eventDateRaw: string | null;
  eventTime: string | null;
  guestCount: number | null;
  guestCountRaw: string | null;
  /** From Arthur's parsed fields; the API doesn't accept edits to it. */
  budget: string | null;
  specialRequests: string | null;
  summary: string | null;
  missingInformation: string[];
  /** Parsed fields the details list doesn't otherwise show. */
  extraFields: ExtraField[];
  transcript: string | null;
  followUpNotes: string | null;
  stage: Stage;
  source: 'call' | 'manual';
  retellCallId: string | null;
  callerPhone: string | null;
  coordinatorEmail: CoordinatorEmail;
  createdAt: string;
  updatedAt: string;
  activity: ActivityItem[];
}

/** Fields PATCH /api/{event,catering}-inquiries/:id/ accepts. */
export interface EditableFields {
  guest_name?: string;
  phone?: string;
  email?: string;
  occasion?: string;
  preferred_date?: string;
  guest_count?: string;
  preferred_time?: string;
  special_requests?: string;
  follow_up_notes?: string;
}

// ---------------------------------------------------------------------------
// List query (URL ⇄ API)
// ---------------------------------------------------------------------------

export type View =
  | 'inbox'
  | 'new'
  | 'to_contact'
  | 'won'
  | 'lost'
  | 'archived'
  | 'all';
export const VIEWS: View[] = [
  'inbox',
  'new',
  'to_contact',
  'won',
  'lost',
  'archived',
  'all',
];
export const VIEW_LABEL: Record<View, string> = {
  inbox: 'Inbox',
  new: 'New',
  to_contact: 'To contact',
  won: 'Won',
  lost: 'Lost',
  archived: 'Archive',
  all: 'All',
};

/** Inbox is the API default (everything not won, lost or archived). */
const VIEW_STATE: Record<View, DashboardState | 'all' | null> = {
  inbox: null,
  new: 'unseen',
  to_contact: 'seen',
  won: 'won',
  lost: 'hidden',
  archived: 'archived',
  all: 'all',
};

export type EmailFilter = 'any' | CoordinatorEmailStatus;
export const EMAIL_FILTERS: EmailFilter[] = [
  'any',
  'sent',
  'failed',
  'pending',
  'processing',
];
export const EMAIL_FILTER_LABEL: Record<EmailFilter, string> = {
  any: 'Any team email',
  sent: 'Email sent',
  failed: 'Email failed',
  pending: 'Email pending',
  processing: 'Email sending',
};

export type SortKey = 'received' | 'guest' | 'occasion' | 'date' | 'activity';
const SORT_FIELD: Record<SortKey, string> = {
  received: 'created_at',
  guest: 'guest_name',
  occasion: 'occasion',
  date: 'preferred_date',
  activity: 'updated_at',
};
const SORT_KEYS = Object.keys(SORT_FIELD) as SortKey[];

export const PAGE_SIZE = 25;

export interface ListQuery {
  view: View;
  email: EmailFilter;
  search: string;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  page: number;
}

/** URL parameters that describe the list; kept when a record opens. */
export const LIST_PARAMS = ['view', 'email', 'q', 'sort', 'page'] as const;

export function parseListQuery(params: URLSearchParams): ListQuery {
  const view = params.get('view') as View;
  const email = params.get('email') as EmailFilter;
  const sort = params.get('sort') || '-received';
  const sortKey = sort.replace(/^-/, '') as SortKey;
  const page = Number(params.get('page'));
  return {
    view: VIEWS.includes(view) ? view : 'inbox',
    email: EMAIL_FILTERS.includes(email) ? email : 'any',
    search: (params.get('q') || '').trim(),
    sortKey: SORT_KEYS.includes(sortKey) ? sortKey : 'received',
    sortDir:
      SORT_KEYS.includes(sortKey) && !sort.startsWith('-') ? 'asc' : 'desc',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** The list endpoint's query string for a list query. */
export function toListParams(q: ListQuery): Record<string, string> {
  const out: Record<string, string> = {
    page: String(q.page),
    page_size: String(PAGE_SIZE),
    ordering: `${q.sortDir === 'desc' ? '-' : ''}${SORT_FIELD[q.sortKey]}`,
  };
  const state = VIEW_STATE[q.view];
  if (state) out.dashboard_state = state;
  if (q.email !== 'any') out.processing_status = q.email;
  if (q.search) out.q = q.search;
  return out;
}

export function keepListParams(params: URLSearchParams): string {
  const next = new URLSearchParams();
  LIST_PARAMS.forEach((k) => {
    const v = params.get(k);
    if (v) next.set(k, v);
  });
  const s = next.toString();
  return s ? `?${s}` : '';
}

export interface SummaryCounts {
  total: number;
  visible: number;
  unseen: number;
  seen: number;
  won: number;
  hidden: number;
  archived: number;
  failedEmail: number;
}

export function viewCount(
  c: SummaryCounts | undefined,
  v: View,
): number | undefined {
  if (!c) return undefined;
  return {
    inbox: c.visible,
    new: c.unseen,
    to_contact: c.seen,
    won: c.won,
    lost: c.hidden,
    archived: c.archived,
    all: c.total,
  }[v];
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];

function iso(y: number, mo: number, d: number): string | null {
  const dt = new Date(y, mo - 1, d);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  )
    return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Best-effort parse of the free-text `preferred_date` into yyyy-mm-dd. */
export function parseEventDate(
  raw?: string | null,
  now = new Date(),
): string | null {
  if (!raw) return null;
  const s = raw.trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return iso(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m) return iso(+m[3] < 100 ? 2000 + +m[3] : +m[3], +m[1], +m[2]);
  m = s
    .toLowerCase()
    .match(/([a-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?/);
  if (m) {
    const mi = MONTHS.indexOf(m[1].slice(0, 3));
    if (mi >= 0) {
      let year = m[3] ? +m[3] : now.getFullYear();
      const candidate = new Date(year, mi, +m[2]);
      if (!m[3] && candidate.getTime() < now.getTime() - 86400000 * 30)
        year += 1;
      return iso(year, mi + 1, +m[2]);
    }
  }
  return null;
}

export function parseGuestCount(raw?: string | number | null): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return raw;
  const m = String(raw).match(/(\d+)(?:\s*(?:-|–|to)\s*(\d+))?/);
  if (!m) return null;
  return m[2] ? Math.round((+m[1] + +m[2]) / 2) : +m[1];
}

export function formatEventDate(
  value: string | null,
  raw: string | null,
  opts: { withYear?: boolean; weekday?: boolean } = {},
): string {
  const day = localDay(value);
  if (!day) return raw || '';
  const [y, m, d] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(undefined, {
    weekday: opts.weekday ? 'short' : undefined,
    month: 'short',
    day: 'numeric',
    year:
      opts.withYear || y !== new Date().getFullYear() ? 'numeric' : undefined,
  }).format(new Date(y, m - 1, d));
}

/**
 * Clock time for display. A trailing AM/PM wins (the editor is free text,
 * placeholder "7:00 PM"). Bare H:MM is 24-hour, so "19:00" is still 7:00 PM.
 */
export function formatTime(raw?: string | null): string {
  if (!raw) return '';
  const m = raw
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(?:([ap])\.?\s*m\.?)?$/i);
  if (!m) return raw;
  const minute = +m[2];
  if (minute > 59) return raw;
  let hour = +m[1];
  const meridiem = m[3]?.toLowerCase();
  if (meridiem) {
    if (hour < 1 || hour > 12) return raw;
    hour = meridiem === 'p' ? (hour % 12) + 12 : hour % 12;
  } else if (hour > 23) {
    return raw;
  }
  const hh = hour % 12 === 0 ? 12 : hour % 12;
  return `${hh}:${m[2]} ${hour >= 12 ? 'PM' : 'AM'}`;
}

/** "today", "in 5 days", "3 days ago" — empty when the date didn't parse. */
export function relativeEventLabel(
  value: string | null,
  now = new Date(),
): string | null {
  const d = daysUntil(value, now);
  if (d == null || Number.isNaN(d)) return null;
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  if (d > 1) return `in ${d} days`;
  return `${Math.abs(d)} days ago`;
}

export function daysUntil(
  value: string | null,
  now = new Date(),
): number | null {
  const day = localDay(value);
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round(
    (new Date(y, m - 1, d).getTime() - start.getTime()) / 86400000,
  );
}

export interface NextAction {
  label: string;
  urgency: 'now' | 'soon' | 'none';
}

/**
 * Closed inquiries have no next step, so `nextAction` leaves the label empty.
 * The detail pane must not treat that empty label as lost.
 */
export function closedStageNote(stage: Stage): string | null {
  if (stage === 'won') return 'Booked — reopen it from the stage menu.';
  if (stage === 'archived') return 'Archived — reopen it from the stage menu.';
  if (stage === 'lost')
    return 'Marked as lost — reopen it from the stage menu.';
  return null;
}

/** The one thing the team should do next. */
export function nextAction(e: InquiryRecord, now = new Date()): NextAction {
  if (!isOpenStage(e.stage)) return { label: '', urgency: 'none' };
  if (e.stage === 'new')
    return { label: 'Review and contact guest', urgency: 'now' };
  const via = (e.preferredContact || '').toLowerCase();
  const verb = via.includes('email')
    ? 'Email guest'
    : via.includes('text')
      ? 'Text guest'
      : 'Call guest';
  const missing = e.missingInformation.slice(0, 2).join(', ');
  const d = daysUntil(e.eventDate, now);
  return {
    label: missing ? `${verb} — collect ${missing}` : verb,
    urgency: d != null && d <= 14 ? 'now' : 'soon',
  };
}

export function titleFor(e: InquiryRecord): string {
  return (
    e.guestName?.trim() || (e.phone ? formatPhone(e.phone) : 'Unknown guest')
  );
}

export function occasionLabel(raw: string | null, fallback: string): string {
  if (!raw?.trim()) return fallback;
  const s = raw.trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "event_time" → "event time". For parsed_fields keys shown to people. */
export function humanizeKey(key: string): string {
  return key.replace(/[_-]+/g, ' ').trim().toLowerCase();
}
